/**
 * StageHistoryRowHelpers — pure helpers used by the StageHistoryRail
 * row. Lives in its own file so StageHistoryRail.tsx stays under the
 * 200-line cap (CC-10).
 */
import { upstreamStagesFor } from './gateGroups';
import type { AgentResult } from '@/types';

// Pull the reviewer's free-text note off edits_json regardless of which
// of the five aliases the FE wrote it under. Returns the trimmed note
// or null when the stage was approved without a comment.
export function extractReviewerNote(
  edits: Record<string, unknown> | null | undefined,
): string | null {
  if (!edits) return null;
  for (const key of ['reviewer_notes', 'reviewer_note', 'note', 'comment', 'notes'] as const) {
    const v = edits[key];
    if (typeof v === 'string' && v.trim()) return v.trim();
    if (Array.isArray(v)) {
      const joined = v.map((x) => String(x)).filter((x) => x.trim()).join('\n');
      if (joined) return joined;
    }
  }
  return null;
}

// Reduce agent_results (which may hold several attempts per stage) to one
// row per stage — the highest attempt. Shared by the history rail and the
// gate roll-up components.
export function latestAttemptByStage(
  agents: AgentResult[],
): Map<string, AgentResult> {
  const byStage = new Map<string, AgentResult>();
  for (const r of agents) {
    const prev = byStage.get(r.agent_name);
    if (!prev || prev.attempt < r.attempt) byStage.set(r.agent_name, r);
  }
  return byStage;
}

// One-line per-stage summary chips (count of clauses / parties / etc.)
// shown on the collapsed row so the reviewer can scan the rail
// without opening every entry.
export function summaryBitsFor(
  name: string,
  output: Record<string, unknown> | null,
): string[] {
  const o = output ?? {};
  const bits: string[] = [];
  const num = (v: unknown): number | null => (typeof v === 'number' ? v : null);
  const str = (v: unknown): string | null => (typeof v === 'string' ? v : null);
  const arr = (v: unknown): unknown[] | null => (Array.isArray(v) ? v : null);
  if (name === 'orchestrator') {
    const cls = str(o.document_class); if (cls) bits.push(`class: ${cls}`);
  } else if (name === 'document_assistant') {
    const cov = num(o.coverage_percent); if (cov != null) bits.push(`OCR ${cov}% coverage`);
    if (o.extraction_complete === false) bits.push('extraction incomplete');
  } else if (name === 'document') {
    const lang = str(o.language); if (lang) bits.push(`lang: ${lang}`);
    const pages = num(o.page_count); if (pages != null) bits.push(`${pages} pages`);
  } else if (name === 'parties') {
    const n = num(o.entity_count); if (n != null) bits.push(`${n} parties`);
  } else if (name === 'clause') {
    const a = arr(o.clauses); if (a) bits.push(`${a.length} clauses`);
  } else if (name === 'compliance') {
    const a = arr(o.findings); if (a) bits.push(`${a.length} findings`);
  } else if (name === 'signature') {
    const a = arr(o.signatures); if (a) bits.push(`${a.length} sigs`);
  } else if (name === 'gate') {
    const dec = str(o.decision); if (dec) bits.push(`decision: ${dec}`);
  }
  return bits;
}

// Overall (0-100) LLM confidence score an agent reports on its own output.
// Used to surface per-agent performance in the gate roll-up. Null-safe:
// returns null for stages with no overall score (e.g. OCR) or missing data.
const CONFIDENCE_FIELD: Record<string, string> = {
  document: 'document_confidence_score',
  parties: 'parties_confidence_score',
  clause: 'clause_confidence_score',
  compliance: 'compliance_confidence_score',
  signature: 'signature_confidence_score',
};

export function overallConfidence(
  stage: string,
  output: Record<string, unknown> | null,
): number | null {
  const field = CONFIDENCE_FIELD[stage];
  if (!field || !output) return null;
  const v = output[field];
  return typeof v === 'number' ? v : null;
}

// Single aggregate confidence for an intermediate gate: the mean of its
// upstream agents' self-reported scores. Agents with no score (e.g. the OCR
// assistant) are excluded. Null when no upstream agent has scored yet.
export function gateConfidence(gate: string, agents: AgentResult[]): number | null {
  const byStage = latestAttemptByStage(agents);
  const scores: number[] = [];
  for (const stage of upstreamStagesFor(gate)) {
    const row = byStage.get(stage);
    const c = row ? overallConfidence(stage, row.output ?? null) : null;
    if (c !== null) scores.push(c);
  }
  if (scores.length === 0) return null;
  return Math.round(scores.reduce((a, b) => a + b, 0) / scores.length);
}

// Map array items (strings or objects) to display labels, picking the first
// present key for object items. Drops blanks.
function toLabels(v: unknown, keys: string[]): string[] {
  if (!Array.isArray(v)) return [];
  return v
    .map((x) => {
      if (typeof x === 'string') return x;
      if (x && typeof x === 'object') {
        const o = x as Record<string, unknown>;
        for (const k of keys) {
          if (typeof o[k] === 'string' && (o[k]).trim()) return o[k];
        }
      }
      return '';
    })
    .filter((s) => s.trim().length > 0);
}

// Small resume of an agent's warnings/flags for the gate roll-up. Common
// source is `flags_for_downstream`; a few high-signal per-stage fields are
// added. Capped so the gate view stays a "small resume".
export function agentWarnings(
  stage: string,
  output: Record<string, unknown> | null,
): string[] {
  const o = output ?? {};
  const out: string[] = [...toLabels(o.flags_for_downstream, [])];

  if (stage === 'document_assistant') {
    const cov = typeof o.coverage_percent === 'number' ? o.coverage_percent : null;
    if (cov !== null && cov < 80) out.push(`OCR coverage low (${cov}%)`);
    if (o.extraction_complete === false) out.push('OCR extraction incomplete');
    out.push(...toLabels(o.warnings, []));
  } else if (stage === 'parties') {
    for (const c of toLabels(o.conflict_flags, ['reason'])) out.push(c);
    // Deterministic counterparty identity checks (party_verification). CMP
    // computes these, not the agent, so they are reproducible — which is why
    // they surface as Gate 1 warnings rather than anything the model wrote.
    if (Array.isArray(o.party_verification)) {
      for (const v of o.party_verification) {
        if (v && typeof v === 'object') {
          out.push(...toLabels((v as Record<string, unknown>).findings, []));
        }
      }
    }
  } else if (stage === 'clause') {
    out.push(...toLabels(o.missing_standard_clauses, ['clause_type', 'name', 'description']).map((m) => `Missing clause: ${m}`));
    out.push(...toLabels(o.highest_risk_clauses, ['clause_type', 'name', 'description']));
  } else if (stage === 'compliance') {
    out.push(...toLabels(o.contractual_compliance_issues, ['issue', 'message', 'description', 'title']));
    out.push(...toLabels(o.missing_compliance_clauses, ['clause_type', 'name', 'description']).map((m) => `Missing: ${m}`));
    const gdpr = (o.gdpr_analysis && typeof o.gdpr_analysis === 'object' ? o.gdpr_analysis : {}) as Record<string, unknown>;
    out.push(...toLabels(gdpr.gdpr_issues, ['issue', 'message', 'description']));
  } else if (stage === 'signature') {
    if (Array.isArray(o.signature_blocks)) {
      for (const b of o.signature_blocks) {
        if (b && typeof b === 'object') out.push(...toLabels((b as Record<string, unknown>).issues, []));
      }
    }
  }

  // De-dupe, keep it small.
  return Array.from(new Set(out)).slice(0, 6);
}
